// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Hand-rolled SVG chart primitives. No chart library — see factory rules. */
import { escapeHtml, formatCompact } from '../utils/format';
import { FUEL_COLOUR, FUEL_LABEL, type Fuel, type Mix } from '../fuels';

export const SVG_NS = 'http://www.w3.org/2000/svg';

export function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

export function niceCeil(max: number): number {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(max));
  const norm = max / mag;
  let step: number;
  if (norm <= 1) step = 1;
  else if (norm <= 2) step = 2;
  else if (norm <= 2.5) step = 2.5;
  else if (norm <= 5) step = 5;
  else step = 10;
  return step * mag;
}

export function ticks(max: number, count = 5): number[] {
  const top = niceCeil(max);
  return Array.from({ length: count + 1 }, (_, i) => (top / count) * i);
}

// ── Horizontal ranked bars ───────────────────────────────────────────────────
export interface HBarDatum {
  id: string;
  label: string;
  value: number;
  colour: string;
  tip: string;
  flag?: string;
}

export function horizontalBars(
  data: HBarDatum[],
  opts: { width?: number; rowHeight?: number; labelWidth?: number; valueFormat?: (v: number) => string; onClick?: (id: string) => void } = {},
): SVGSVGElement {
  const width = opts.width ?? 900;
  const rowH = opts.rowHeight ?? 26;
  const labelW = opts.labelWidth ?? 190;
  const fmt = opts.valueFormat ?? ((v: number) => formatCompact(v));
  const valueW = 66;
  const barMax = Math.max(60, width - labelW - valueW - 16);
  const height = Math.max(1, data.length) * rowH + 8;
  const max = niceCeil(Math.max(...data.map((d) => d.value), 0.0001));

  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  data.forEach((d, i) => {
    const y = i * rowH + 4;
    const g = el('g');
    const label = el('text', { x: labelW - 8, y: y + rowH / 2, 'text-anchor': 'end', 'dominant-baseline': 'middle', 'font-size': 11.5 });
    label.textContent = d.label;
    g.appendChild(label);
    g.appendChild(el('rect', { x: labelW, y: y + 2, width: barMax, height: rowH - 8, rx: 3, fill: 'var(--bg-elevated)' }));
    const w = Math.max(1, (d.value / max) * barMax);
    const bar = el('rect', { class: 'bar', x: labelW, y: y + 2, width: w, height: rowH - 8, rx: 3, fill: d.colour, 'data-tip': d.tip, 'aria-label': d.tip, tabindex: 0, role: 'button' });
    g.appendChild(bar);
    const val = el('text', { class: 'val-label', x: labelW + barMax + 8, y: y + rowH / 2, 'dominant-baseline': 'middle' });
    val.textContent = fmt(d.value) + (d.flag ? ` ${d.flag}` : '');
    g.appendChild(val);
    const hit = el('rect', { x: 0, y, width, height: rowH, fill: 'transparent', style: 'cursor:pointer', 'data-tip': d.tip });
    g.appendChild(hit);
    if (opts.onClick) {
      const fire = () => opts.onClick!(d.id);
      g.addEventListener('click', fire);
      bar.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
          e.preventDefault();
          fire();
        }
      });
    }
    svg.appendChild(g);
  });
  return svg;
}

// ── Vertical column chart ─────────────────────────────────────────────────────
export interface ColumnDatum {
  id: string;
  label: string;
  value: number;
  colour: string;
  tip: string;
}
export interface ColumnOpts {
  width?: number;
  height?: number;
  labelEvery?: number;
  onClick?: (id: string) => void;
  annotations?: Array<{ atLabel: string; text: string; colour?: string }>;
  valueFormat?: (v: number) => string;
}

export function columnChart(data: ColumnDatum[], opts: ColumnOpts = {}): SVGSVGElement {
  const width = opts.width ?? 960;
  const height = opts.height ?? 340;
  const m = { top: 18, right: 16, bottom: 44, left: 56 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const max = niceCeil(Math.max(...data.map((d) => d.value), 0.0001));
  const fmt = opts.valueFormat ?? ((v: number) => formatCompact(v));
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  for (const t of ticks(max, 4)) {
    const y = m.top + plotH - (t / max) * plotH;
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 8, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = fmt(t);
    svg.appendChild(lbl);
  }
  const slot = plotW / Math.max(1, data.length);
  const barW = Math.max(1, slot * 0.78);
  const labelEvery = opts.labelEvery ?? Math.max(1, Math.ceil(data.length / 14));
  data.forEach((d, i) => {
    const x = m.left + i * slot + (slot - barW) / 2;
    const h = Math.max(0.5, (d.value / max) * plotH);
    const y = m.top + plotH - h;
    const bar = el('rect', { class: 'bar', x, y, width: barW, height: h, rx: Math.min(2, barW / 3), fill: d.colour, 'data-tip': d.tip, 'aria-label': d.tip });
    if (opts.onClick) {
      bar.style.cursor = 'pointer';
      bar.addEventListener('click', () => opts.onClick!(d.id));
    }
    svg.appendChild(bar);
    if (barW < 8) svg.appendChild(el('rect', { x: m.left + i * slot, y: m.top, width: slot, height: plotH, fill: 'transparent', 'data-tip': d.tip }));
    if (i % labelEvery === 0) {
      const lbl = el('text', { class: 'axis-label', x: x + barW / 2, y: height - m.bottom + 14, 'text-anchor': 'middle' });
      lbl.textContent = d.label;
      svg.appendChild(lbl);
    }
  });
  for (const a of opts.annotations ?? []) {
    const idx = data.findIndex((d) => d.id === a.atLabel);
    if (idx < 0) continue;
    const x = m.left + idx * slot + slot / 2;
    const colour = a.colour ?? 'var(--accent-secondary)';
    svg.appendChild(el('line', { x1: x, y1: m.top, x2: x, y2: m.top + plotH, stroke: colour, 'stroke-width': 1.2, 'stroke-dasharray': '3 3', opacity: 0.85 }));
    svg.appendChild(el('circle', { cx: x, cy: m.top, r: 4, fill: colour, 'data-tip': a.text, style: 'cursor:help' }));
  }
  svg.appendChild(el('line', { class: 'axis-line', x1: m.left, y1: m.top + plotH, x2: m.left + plotW, y2: m.top + plotH }));
  return svg;
}

// ── 100%-stacked fuel area by year ────────────────────────────────────────────
// Bottom→top order puts the electric wedge against the top edge so it visibly
// grows in recent years.
const STACK_ORDER: Fuel[] = ['petrol', 'diesel', 'other', 'hybrid', 'bev'];

export function stacked100Area(
  data: Array<{ year: number; mix: Mix }>,
  opts: { width?: number; height?: number; annotations?: Array<{ year: number; text: string }> } = {},
): SVGSVGElement {
  const width = opts.width ?? 960;
  const height = opts.height ?? 360;
  const m = { top: 16, right: 14, bottom: 34, left: 42 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const pts = data.filter((d) => d.mix.total > 0);
  const n = pts.length;
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  const xAt = (i: number) => m.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (frac: number) => m.top + plotH - frac * plotH;

  for (const t of [0, 25, 50, 75, 100]) {
    const y = yAt(t / 100);
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 6, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = `${t}%`;
    svg.appendChild(lbl);
  }

  // Cumulative fraction boundaries per point.
  const bounds = pts.map((d) => {
    const acc: Record<string, number> = {};
    let cum = 0;
    for (const f of STACK_ORDER) {
      const frac = d.mix.total > 0 ? d.mix[f] / d.mix.total : 0;
      acc[`${f}_lo`] = cum;
      cum += frac;
      acc[`${f}_hi`] = cum;
    }
    return acc;
  });

  for (const f of STACK_ORDER) {
    const top = pts.map((_, i) => `${xAt(i).toFixed(1)},${yAt(bounds[i][`${f}_hi`]).toFixed(1)}`);
    const bot = pts.map((_, i) => `${xAt(i).toFixed(1)},${yAt(bounds[i][`${f}_lo`]).toFixed(1)}`).reverse();
    const path = `M${top.join(' L')} L${bot.join(' L')} Z`;
    svg.appendChild(el('path', { d: path, fill: FUEL_COLOUR[f], opacity: 0.92 }));
  }

  // Per-point hover columns with the full mix.
  pts.forEach((d, i) => {
    const x = xAt(i);
    const tipLines = STACK_ORDER.slice().reverse().map((f) => `${FUEL_LABEL[f]}: ${((d.mix[f] / d.mix.total) * 100).toFixed(1)}%`);
    const slotW = n <= 1 ? plotW : plotW / (n - 1);
    svg.appendChild(el('rect', { x: x - slotW / 2, y: m.top, width: slotW, height: plotH, fill: 'transparent', 'data-tip': `${d.year}\n${tipLines.join('\n')}` }));
    if (i % Math.max(1, Math.ceil(n / 12)) === 0 || i === n - 1) {
      const lbl = el('text', { class: 'axis-label', x, y: height - m.bottom + 14, 'text-anchor': 'middle' });
      lbl.textContent = `’${String(d.year).slice(2)}`;
      svg.appendChild(lbl);
    }
  });

  for (const a of opts.annotations ?? []) {
    const i = pts.findIndex((d) => d.year === a.year);
    if (i < 0) continue;
    const x = xAt(i);
    svg.appendChild(el('line', { x1: x, y1: m.top, x2: x, y2: m.top + plotH, stroke: '#0f172a', 'stroke-width': 1, 'stroke-dasharray': '2 3', opacity: 0.5 }));
    svg.appendChild(el('circle', { cx: x, cy: m.top + 4, r: 3.5, fill: '#0f172a', 'data-tip': a.text, style: 'cursor:help' }));
  }
  return svg;
}

// ── Multi-line chart (trend of a % metric) ────────────────────────────────────
export interface LineSeries {
  id: string;
  label: string;
  colour: string;
  values: Array<number | null>;
}

export function multiLine(
  xLabels: Array<string | number>,
  series: LineSeries[],
  opts: { width?: number; height?: number; yMax?: number; yFmt?: (v: number) => string; onPoint?: (sid: string) => void } = {},
): SVGSVGElement {
  const width = opts.width ?? 960;
  const height = opts.height ?? 360;
  const m = { top: 16, right: 96, bottom: 34, left: 46 };
  const plotW = width - m.left - m.right;
  const plotH = height - m.top - m.bottom;
  const n = xLabels.length;
  const allVals = series.flatMap((s) => s.values.filter((v): v is number => v != null));
  const yMax = opts.yMax ?? niceCeil(Math.max(...allVals, 0.0001));
  const yFmt = opts.yFmt ?? ((v: number) => formatCompact(v));
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });

  const xAt = (i: number) => m.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (v: number) => m.top + plotH - (v / yMax) * plotH;

  for (const t of ticks(yMax, 4)) {
    const y = yAt(t);
    svg.appendChild(el('line', { class: 'grid-line', x1: m.left, y1: y, x2: m.left + plotW, y2: y }));
    const lbl = el('text', { class: 'axis-label', x: m.left - 6, y, 'text-anchor': 'end', 'dominant-baseline': 'middle' });
    lbl.textContent = yFmt(t);
    svg.appendChild(lbl);
  }
  xLabels.forEach((xl, i) => {
    if (i % Math.max(1, Math.ceil(n / 12)) === 0 || i === n - 1) {
      const lbl = el('text', { class: 'axis-label', x: xAt(i), y: height - m.bottom + 14, 'text-anchor': 'middle' });
      lbl.textContent = String(xl);
      svg.appendChild(lbl);
    }
  });
  svg.appendChild(el('line', { class: 'axis-line', x1: m.left, y1: m.top + plotH, x2: m.left + plotW, y2: m.top + plotH }));

  for (const s of series) {
    const pts = s.values.map((v, i) => (v == null ? null : `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`)).filter((p): p is string => p != null);
    if (pts.length === 0) continue;
    const line = el('polyline', { points: pts.join(' '), fill: 'none', stroke: s.colour, 'stroke-width': 2.2, 'stroke-linejoin': 'round', 'stroke-linecap': 'round' });
    if (opts.onPoint) line.style.cursor = 'pointer';
    svg.appendChild(line);
    s.values.forEach((v, i) => {
      if (v == null) return;
      const dot = el('circle', { cx: xAt(i), cy: yAt(v), r: 3, fill: s.colour, 'data-tip': `${s.label} · ${xLabels[i]}: ${yFmt(v)}`, style: opts.onPoint ? 'cursor:pointer' : '' });
      if (opts.onPoint) dot.addEventListener('click', () => opts.onPoint!(s.id));
      svg.appendChild(dot);
    });
    // End label.
    const lastIdx = s.values.map((v, i) => (v != null ? i : -1)).filter((i) => i >= 0).pop();
    if (lastIdx != null) {
      const lbl = el('text', { x: xAt(lastIdx) + 8, y: yAt(s.values[lastIdx] as number), 'dominant-baseline': 'middle', 'font-size': 11, 'font-weight': 600, fill: s.colour });
      lbl.textContent = s.label;
      svg.appendChild(lbl);
    }
  }
  return svg;
}

// ── Donut (fuel mix) ──────────────────────────────────────────────────────────
export function fuelDonut(mix: Mix, order: Fuel[], opts: { size?: number } = {}): SVGSVGElement {
  const size = opts.size ?? 200;
  const r = size / 2;
  const inner = r * 0.58;
  const cx = r;
  const cy = r;
  const svg = el('svg', { class: 'chart', viewBox: `0 0 ${size} ${size}`, width: size, height: size, role: 'img' });
  const total = mix.total || 1;
  let a0 = -Math.PI / 2;
  for (const f of order) {
    const frac = mix[f] / total;
    if (frac <= 0) continue;
    const a1 = a0 + frac * Math.PI * 2;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    const xi0 = cx + inner * Math.cos(a1);
    const yi0 = cy + inner * Math.sin(a1);
    const xi1 = cx + inner * Math.cos(a0);
    const yi1 = cy + inner * Math.sin(a0);
    const d = `M${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 ${large} 1 ${x1.toFixed(2)},${y1.toFixed(2)} L${xi0.toFixed(2)},${yi0.toFixed(2)} A${inner},${inner} 0 ${large} 0 ${xi1.toFixed(2)},${yi1.toFixed(2)} Z`;
    svg.appendChild(el('path', { d, fill: FUEL_COLOUR[f], stroke: 'var(--bg-panel)', 'stroke-width': 1.5, 'data-tip': `${FUEL_LABEL[f]}: ${(frac * 100).toFixed(1)}% (${Math.round(mix[f]).toLocaleString()})`, style: 'cursor:default' }));
    a0 = a1;
  }
  return svg;
}

// ── Green sequential ramp for the choropleth ──────────────────────────────────
// Starts at a visible mint (not near-white): fuel shares are heavily right-skewed,
// so a near-white low end washed the whole map out. Break points are set per
// metric from the real quantiles so each band carries a meaningful slice.
export const RAMP = ['#d7f2e0', '#a7e8bf', '#6fd79a', '#38c172', '#16a34a', '#128539', '#0a5a28'];

export function rampColour(value: number | null, stops: number[]): string {
  if (value == null || !Number.isFinite(value)) return '#e2e8f0';
  for (let i = 0; i < stops.length; i++) {
    if (value < stops[i]) return RAMP[i];
  }
  return RAMP[RAMP.length - 1];
}

/** Inline 100%-stacked fuel mini-bar for table rows (HTML string). */
export function mixSparkHtml(mix: Mix, order: Fuel[]): string {
  if (mix.total <= 0) return '<span class="spark-mix"></span>';
  const segs = order
    .map((f) => (mix[f] > 0 ? `<span style="flex:${mix[f]};background:${FUEL_COLOUR[f]}"></span>` : ''))
    .join('');
  return `<span class="spark-mix" aria-hidden="true">${segs}</span>`;
}

/** Full-width 100%-stacked fuel bar with hover tooltips (HTML string). */
export function mixBarHtml(mix: Mix, order: Fuel[], opts: { tall?: boolean } = {}): string {
  if (mix.total <= 0) return '<div class="mix-bar"></div>';
  const segs = order
    .map((f) => {
      if (mix[f] <= 0) return '';
      const pct = (mix[f] / mix.total) * 100;
      const tip = escapeHtml(`${FUEL_LABEL[f]}: ${pct.toFixed(1)}% (${Math.round(mix[f]).toLocaleString()})`);
      return `<div class="mix-seg" style="flex:${mix[f]};background:${FUEL_COLOUR[f]}" data-tip="${tip}" aria-label="${tip}"></div>`;
    })
    .join('');
  return `<div class="mix-bar${opts.tall ? ' tall' : ''}">${segs}</div>`;
}

/** A fuel legend (HTML string). */
export function fuelLegendHtml(order: Fuel[]): string {
  return (
    '<div class="legend">' +
    order.map((f) => `<span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${FUEL_COLOUR[f]}"></span>${FUEL_LABEL[f]}</span>`).join('') +
    '</div>'
  );
}

export { escapeHtml };
